import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { authService } from '../api/auth';
import { otpService } from '../api/otp';
import { isCustomerAuthenticated as readCustomerSession } from '../utils/customerAuth';
import { devLog } from '../utils/devLog';

interface User {
  _id: string;
  email: string;
  name?: string;
  firstName?: string;
  lastName?: string;
  role?: string;
  phone?: string;
  user_price_level?: 'b2c' | 'b2b' | 'vip';
}

interface AuthContextType {
  user: User | null;
  adminUser: User | null;
  customerPriceLevel: 'b2c' | 'b2b' | 'vip';
  isAuthenticated: boolean;
  isCustomerAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  login: (email: string, password: string) => Promise<void>;
  phoneLogin: (phone: string, otp: string, firebaseIdToken?: string) => Promise<void>;
  logout: () => Promise<void>;
  adminLogout: () => Promise<void>;
  checkAuth: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // Initialize user state directly from localStorage (synchronous)
  const getInitialUser = (): { user: User | null; adminUser: User | null } => {
    try {
      // Check for regular user
      const userToken = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      devLog('[AuthContext] Initial user check - hasToken:', !!userToken, 'hasUser:', !!userStr);

      // Check for admin user
      const adminToken = localStorage.getItem('adminToken');
      const adminUserStr = localStorage.getItem('adminUser');
      devLog('[AuthContext] Initial admin check - hasToken:', !!adminToken, 'hasUser:', !!adminUserStr);
      
      return {
        user: userToken && userStr ? JSON.parse(userStr) : null,
        adminUser: adminToken && adminUserStr ? JSON.parse(adminUserStr) : null
      };
    } catch (error) {
      console.error('[AuthContext] Error reading initial user', error);
      return { user: null, adminUser: null };
    }
  };

  const initialState = getInitialUser();
  const [user, setUser] = useState<User | null>(initialState.user);
  const [adminUser, setAdminUser] = useState<User | null>(initialState.adminUser);
  const [isLoading, setIsLoading] = useState(true);

  const checkAuth = useCallback(() => {
    let savedUser: User | null = null;
    let savedAdminUser: User | null = null;
    try {
      const userToken = localStorage.getItem('token');
      const userStr = localStorage.getItem('user');
      if (userStr) {
        try {
          savedUser = JSON.parse(userStr);
        } catch {
          savedUser = null;
        }
      }
      if (!userToken || !userStr) savedUser = null;

      const adminToken = localStorage.getItem('adminToken');
      const adminUserStr = localStorage.getItem('adminUser');
      if (adminUserStr) {
        try {
          savedAdminUser = JSON.parse(adminUserStr);
        } catch {
          savedAdminUser = null;
        }
      }
      if (!adminToken || !adminUserStr) savedAdminUser = null;
    } catch (e) {
      console.error('checkAuth storage read error', e);
    }

    const userToken = localStorage.getItem('token');
    const adminToken = localStorage.getItem('adminToken');

    devLog('[AuthContext] checkAuth:', {
      hasUserToken: !!userToken,
      hasUser: !!savedUser,
      hasAdminToken: !!adminToken,
      hasAdminUser: !!savedAdminUser,
    });

    setUser(savedUser || null);
    setAdminUser(savedAdminUser || null);
    setIsLoading(false);
  }, []);

  useEffect(() => {
    devLog('[AuthContext] Initializing...');
    checkAuth();

    const verifyCustomerSession = async () => {
      if (!localStorage.getItem('token')) {
        setIsLoading(false);
        return;
      }
      try {
        await authService.verifySession();
      } catch {
        setUser(null);
      } finally {
        setIsLoading(false);
      }
    };
    void verifyCustomerSession();

    // Listen for storage changes (e.g., login from another tab)
    const handleStorageChange = (e: StorageEvent) => {
      const watchedKeys = ['token', 'user', 'adminToken', 'adminUser'];
      if (!e.key || watchedKeys.includes(e.key)) {
        if (process.env.NODE_ENV !== 'production') {
          console.log('[AuthContext] Auth storage changed, re-checking auth...', e.key);
        }
        checkAuth();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    const onLocalAuthChange = () => checkAuth();
    window.addEventListener('nls-auth-changed', onLocalAuthChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('nls-auth-changed', onLocalAuthChange);
    };
  }, [checkAuth]);

  const login = async (email: string, password: string) => {
    devLog('[AuthContext] Starting login process...');
    const result = await authService.loginCustomerOnly({ email, password });

    devLog('[AuthContext] login API result:', {
      success: result.success,
      hasToken: !!result.token,
      hasUser: !!result.user,
      userRole: result.user?.role,
    });

    if (result.success && result.token && result.user) {
      checkAuth();
    } else {
      console.error('[AuthContext] Login failed', result);
      throw new Error(result.message || 'Login failed');
    }
  };

  const phoneLogin = async (phone: string, otp: string, firebaseIdToken?: string) => {
    devLog('[AuthContext] Starting phone login process...');
    const result = await otpService.phoneLogin(phone, otp, firebaseIdToken);

    devLog('[AuthContext] phoneLogin API result:', {
      success: result.success,
      userExists: result.userExists,
      hasToken: !!result.token,
      hasUser: !!result.user,
    });

    // Handle case where user doesn't exist (new user registration)
    if (result.success && !result.userExists) {
      devLog('[AuthContext] New user - registration required');
      throw new Error('REGISTRATION_REQUIRED');
    }

    if (result.success && result.token && result.user) {
      checkAuth();
      devLog('[AuthContext] Phone login successful');
    } else {
      console.error('[AuthContext] Phone login failed', result);
      throw new Error(result.message || 'Phone login failed');
    }
  };

  const logout = async () => {
    await authService.logout();
    setUser(null);
    checkAuth();
  };

  const adminLogout = async () => {
    await authService.adminLogout();
    setAdminUser(null);
    checkAuth();
  };

  const customerPriceLevel: 'b2c' | 'b2b' | 'vip' =
    user?.user_price_level === 'b2b' || user?.user_price_level === 'vip'
      ? user.user_price_level
      : 'b2c';

  return (
    <AuthContext.Provider
      value={{
        user,
        adminUser,
        customerPriceLevel,
        isAuthenticated: !!user || !!adminUser,
        isCustomerAuthenticated: readCustomerSession() || (!!user && user.role !== 'admin'),
        isAdmin: !!adminUser,
        isLoading,
        login,
        phoneLogin,
        logout,
        adminLogout,
        checkAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
